import classNames from 'classnames';
import { type CSSProperties, type ReactNode, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './Expandable.module.css';
import { useResizeObserver } from '../../hooks';
export type ExpandableProps = {
  children?: ReactNode;
  open: boolean;
  className?: string;
  contentClassName?: string;
  /**
   * Reserves a small amount of vertical space around the content when open so
   * focus outlines / rings on inputs at the top or bottom edge are not clipped.
   */
  reserveOutlineSpace?: boolean;
};
export type ExpandableRef = {
  checkSize: () => void;
};
const Expandable = forwardRef<ExpandableRef, ExpandableProps>(function Expandable({
  children,
  open,
  className,
  contentClassName,
  reserveOutlineSpace = false
}: ExpandableProps, forwardedRef) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);
  const setHeightFromRef = useCallback(() => {
    if (!ref.current) return;
    const element = ref.current;
    setHeight(element.scrollHeight);
  }, []);
  useImperativeHandle(forwardedRef, () => ({
    checkSize: setHeightFromRef
  }));
  useResizeObserver({
    ref,
    onResize: setHeightFromRef
  });
  useEffect(() => {
    if (!open) return;
    setHeightFromRef();
    window.addEventListener('resize', setHeightFromRef);
    return () => {
      window.removeEventListener('resize', setHeightFromRef);
    };
  }, [setHeightFromRef, open]);
  return <div className={classNames(styles.container, className, {
    [styles.open!]: open,
    [styles.outlineSpace!]: reserveOutlineSpace
  })} style={{
    '--height': `${height}px`
  } as CSSProperties}>
      <div className={classNames(styles.content, contentClassName)} ref={ref}>
        {children}
      </div>
    </div>;
});
export default Expandable;